import { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { depascalize, SortOrder, listFiltersService, ListTableSort } from "sprint-common-web";

import { ContentSource } from "../../../common/types/ContentSource";

export const sourceOptions = Object.values(ContentSource)
    .filter((v): v is ContentSource => typeof v === "number")
    .map(v => ({ key: v, text: depascalize(ContentSource[v]) }));

export interface MovieListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    title?: string;
    sources?: ContentSource[];
    includeMerged: boolean;
}

const defaultFilters: MovieListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "title",
    sortOrder: "asc",
    title: "",
    includeMerged: false,
    sources: undefined
};

export const useMovieListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<MovieListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<MovieListingFilters>(() => filtersService.getSavedFilters(location.search));

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({ sortBy: sort.by, sortOrder: sort.order, page: 1 });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setTitle = (title?: string): void => {
        saveAndSetFilters({ title, page: 1 });
    };

    const setSources = (sources?: ContentSource[]): void => {
        saveAndSetFilters({ sources, page: 1 });
    };

    const setIncludeMerged = (includeMerged: boolean): void => {
        saveAndSetFilters({ includeMerged, page: 1 });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultFilters);
    };

    const saveAndSetFilters = (newFilters: Partial<MovieListingFilters>): void => {
        setFilters((prevFilters: MovieListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        setIncludeMerged,
        setSources,
        clearFilters
    };
};
